/**
 * Client-side money: formatting and the two conversions the UI is allowed to do.
 *
 * Mirrors backend/src/game/currency/money.ts, and deliberately owns NO rate
 * table. Rates arrive from the server (`GET /shop/wallet` → `currency`, or
 * `GET /account/currency`); a client-side copy would drift the moment an
 * operator edits the table and would price a checkout differently from the
 * charge the server then takes.
 *
 * Two rules, same as the backend's:
 *
 *  1. AMOUNTS ARE INTEGER MINOR UNITS. Nothing here holds "4.99"; a euro float
 *     costing an eighth of a cent per operation is how a checkout total ends up
 *     one cent under the price the player was shown. Even formatting avoids the
 *     divide — the decimal string is assembled from the integer's digits and
 *     handed to Intl, which parses decimal strings exactly.
 *
 *  2. A HISTORICAL AMOUNT IS FORMATTED FROM ITS STORED RATE, never from today's.
 *     `formatFrozen` takes the payment's own frozen `charged` money and does not
 *     look at the live currency at all — a receipt must still say what it said.
 */

/** An amount and the currency it is in. Amount is always integer minor units. */
export interface Money {
  amountMinor: number;
  currency: string;
}

/** The player's display currency, as served by the backend CurrencyService. */
export interface DisplayCurrency {
  /** The accounting currency — EUR. Sent so the client need not assume it. */
  base: string;
  code: string;
  name: string;
  symbol: string;
  /** Units of `code` per 1 base unit. The base currency itself is exactly 1. */
  rate: number;
  /** Decimal places of `code`; amounts travel as integers of this many places. */
  minorUnits: number;
  /** Which revision of the operator's rate table `rate` came from. */
  rateDate: string;
  /** Where the rate came from ('manual', a provider kind, or the shipped table). */
  source?: string;
  /**
   * True when the server considers the rate untrustworthy — the invented shipped
   * table, or older than the operator's max age. The server then settles in the
   * BASE currency, so a display figure carrying this flag is an estimate and the
   * UI must say so rather than quote it as the price.
   */
  stale?: boolean;
}

/** The FX facts frozen onto a stored payment, as sent with a transaction row. */
export interface FrozenFiat {
  /** What was actually charged, in the currency it was charged in. */
  charged: Money;
  /** The accounting value of that charge, in the base currency. */
  base: Money;
  rate: number;
  rateDate: string;
}

export const BASE_CURRENCY = 'EUR';

/** Used only until the first wallet/profile response lands. Rate 1 = base. */
export const BASE_DISPLAY: DisplayCurrency = {
  base: BASE_CURRENCY, code: BASE_CURRENCY, name: 'Euro', symbol: '€',
  rate: 1, minorUnits: 2, rateDate: '',
};

/**
 * One locale for every currency, on purpose.
 *
 * The game's UI is English throughout, and letting each currency pick its home
 * locale would mix decimal separators inside a single screen — "€4.99" next to
 * "4,99 zł" in the same list. Intl still decides the symbol and which side it
 * goes on (¥838 with no decimals, A$ rather than a bare $, "CHF 4.99" with the
 * code leading), which is the part that must not be hand-rolled.
 */
const LOCALE = 'en-US';

const formatters = new Map<string, Intl.NumberFormat>();

function formatterFor(code: string): Intl.NumberFormat {
  let f = formatters.get(code);
  if (!f) {
    // NOT `currencyDisplay: 'narrowSymbol'`: narrow renders AUD and CAD as a bare
    // "$", so an Australian player's checkout was indistinguishable from a US
    // one. The default keeps A$ / CA$.
    f = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: code });
    formatters.set(code, f);
  }
  return f;
}

/** Decimal places of a currency, straight from ICU — JPY is 0, EUR is 2. Used
 *  when no server-sent `minorUnits` is at hand, so there is still no table here. */
export function minorUnitsOf(code: string): number {
  try {
    return formatterFor(code).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

function unitsOf(currency: string | DisplayCurrency): { code: string; minorUnits: number } {
  if (typeof currency === 'string') return { code: currency, minorUnits: minorUnitsOf(currency) };
  return { code: currency.code, minorUnits: currency.minorUnits };
}

/**
 * 499 → "4.99", 838 → "838" (0 decimals), -5 → "-0.05".
 *
 * Exact string surgery rather than `amountMinor / 100`, because the division is
 * where a float creeps in: 1_000_000_07 / 100 is not 1000000.07 in binary, and
 * a big lifetime-deposited figure rendered a cent off.
 */
export function minorToDecimalString(amountMinor: number, minorUnits: number): string {
  const sign = amountMinor < 0 ? '-' : '';
  const digits = String(Math.abs(Math.trunc(amountMinor))).padStart(minorUnits + 1, '0');
  if (minorUnits === 0) return sign + digits;
  return `${sign}${digits.slice(0, -minorUnits)}.${digits.slice(-minorUnits)}`;
}

/**
 * "7.96" → 796. The inverse of minorToDecimalString, for a typed amount.
 *
 * Parses digits rather than doing `Number(text) * 100`: 7.96 * 100 is
 * 795.9999999999999, and truncating that charged a cent short. A comma is
 * accepted as the decimal mark — most of Europe types it that way. Extra typed
 * decimals are TRUNCATED, not rounded, so a half-typed number never jumps up.
 */
export function parseMinor(text: string, minorUnits: number): number {
  const cleaned = String(text).replace(',', '.').replace(/[^0-9.]/g, '');
  if (!cleaned) return 0;
  const [whole, frac = ''] = cleaned.split('.');
  const scaled = (whole || '0') + frac.padEnd(minorUnits, '0').slice(0, minorUnits);
  const n = Number(scaled);
  return Number.isFinite(n) ? n : 0;
}

/** "€4.99" / "¥838" / "A$8.23". The only way money reaches the DOM. */
export function formatMoney(amountMinor: number, currency: string | DisplayCurrency): string {
  const { code, minorUnits } = unitsOf(currency);
  const decimal = minorToDecimalString(amountMinor, minorUnits);
  try {
    // Intl.NumberFormat#format accepts a decimal STRING and parses it exactly —
    // passing Number(decimal) would undo the point of building the string.
    return formatterFor(code).format(decimal as unknown as number);
  } catch {
    // An unknown code must still render something with its currency attached; a
    // bare number is the one output this module exists to prevent.
    return `${decimal} ${code}`;
  }
}

/** Same, for a `Money` as the API sends it. */
export function formatMoneyValue(m: Money): string {
  return formatMoney(m.amountMinor, m.currency);
}

/**
 * A stored payment, rendered from its OWN frozen rate.
 *
 * `charged` was computed and saved when the payment was taken, so this is a pure
 * read — today's table is never consulted. Historical rows re-priced from the
 * live rate would silently restate invoices the player already holds.
 */
export function formatFrozen(fx: FrozenFiat): string {
  return formatMoneyValue(fx.charged);
}

/** "$5.45 (€5.00)" — the charge plus its accounting value, when they differ. */
export function formatFrozenWithBase(fx: FrozenFiat): string {
  const charged = formatMoneyValue(fx.charged);
  if (fx.charged.currency === fx.base.currency) return charged;
  return `${charged} (${formatMoneyValue(fx.base)})`;
}

// ─── conversion (presentation only; the server prices every real charge) ─────

/** Round half away from zero — so a converted refund and its charge still
 *  cancel, which Math.round's toward-+Infinity tie-break broke by sign. */
export function roundMinor(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * The rate is quoted in whole units per whole base unit, but we convert MINOR
 * units and not every currency has two decimals: €4.99 at 168 JPY/EUR is ¥838,
 * not ¥83,832. This bridges the two scales — the same fix the backend carries.
 */
function minorScale(c: DisplayCurrency): number {
  return 10 ** (c.minorUnits - BASE_DISPLAY.minorUnits);
}

/** Base minor units (EUR cents) → minor units of the display currency. */
export function fromBaseMinor(baseMinor: number, c: DisplayCurrency): number {
  if (c.code === c.base) return baseMinor;
  return roundMinor(baseMinor * c.rate * minorScale(c));
}

/** Minor units of the display currency → base minor units (EUR cents). */
export function toBaseMinor(amountMinor: number, c: DisplayCurrency): number {
  if (c.code === c.base) return amountMinor;
  return roundMinor(amountMinor / (c.rate * minorScale(c)));
}

/** Format a base-currency (EUR cent) amount in the player's chosen currency. */
export function formatBaseAs(baseMinor: number, c: DisplayCurrency): string {
  return formatMoney(fromBaseMinor(baseMinor, c), c);
}
